import {useState, useEffect, useRef} from 'react';
import styled from 'styled-components';
import TextAndImageBlock from '@components/TextAndImageBlock';
import {CategoryBar, CategoryLink} from '@components/CategoryBarComponent';
import {ArticleCard} from '@components/Card';
import theme from '@design/theme';
import {query, color} from '@design/templateFns';
import {mq} from '@design/query';
import Block from '@components/Layout/Block';
import {Dropdown} from '@components/DropDownComponent';
import {Pagination} from '@components/Pagination';
import type {BasicText, ResolvedImageData} from '@data/types';

import type {TextAndImageBlockProps} from '@components/TextAndImageBlock';

interface ArticleCardProps {
  image: ResolvedImageData;
  category: string;
  headline: string;
  date?: string;
  description: BasicText | string | JSX.Element;
  url: string;
}

export interface CardGridProps {
  links: CategoryLink[];
  articleCardArr: ArticleCardProps[];
  textAndImageObject?: TextAndImageBlockProps;
}

// PAGINATION MAX NUMBER
const maxNumberInPage = 9; // Show 9 cards on card grid at a time

export const CardGrid = ({
  links,
  articleCardArr,
  textAndImageObject,
}: CardGridProps): JSX.Element => {
  // Ref to scrollTo
  const scrollRef = useRef(null);

  // PAGINATION HANDLING
  const [currentPage, handleCurrentPage] = useState(1); // Current page for pagination
  const [currentCards, handleCurrentCards] = useState([]);
  const [selected, setCategory] = useState(null); // Used for filters

  // Filters Selected
  const filtersOn = selected !== null && !selected.includes('All');

  const calculateTotalCards = (cards) => {
    return filtersOn
      ? cards.filter((c) => selected === c.category).length
      : cards.length;
  };
  const totalCards = calculateTotalCards(articleCardArr);
  const totalNumberOfPages = Math.ceil(totalCards / maxNumberInPage);

  // Creates new sliced array based on filters, no filters, pagination, whatever
  const handleConstructCurrentCards = (cards, total, currentPg) => {
    const currentArr = cards.slice((currentPg - 1) * total, currentPg * total);
    handleCurrentCards(currentArr);
  };

  // Construct initial array
  useEffect(() => {
    handleConstructCurrentCards(articleCardArr, maxNumberInPage, 1);
  }, []);

  // Update cards to show based on filter
  useEffect(() => {
    // Get new set of cards
    if (filtersOn) {
      const filteredCards = articleCardArr.filter((c) => {
        return selected === c.category;
      });
      handleConstructCurrentCards(filteredCards, maxNumberInPage, 1);
    } else {
      // Show first 9 cards
      handleConstructCurrentCards(articleCardArr, maxNumberInPage, 1);
    }

    // Reset pagination
    handleCurrentPage(1);
  }, [selected]);

  // Repeated code... but this is for updating
  // when switching pages.
  useEffect(() => {
    if (filtersOn) {
      const filteredCards = articleCardArr.filter((c) => {
        return selected === c.category;
      });
      handleConstructCurrentCards(filteredCards, maxNumberInPage, currentPage);
    } else {
      // Show first 9 cards
      handleConstructCurrentCards(articleCardArr, maxNumberInPage, currentPage);
    }
  }, [currentPage]);

  // Selecting pagination pages
  // or clicking on forward/back arrows
  const handleSelectPage = (p: number): null => {
    // Scroll to top of card
    scrollRef.current.scrollIntoView();
    handleCurrentPage(p);

    return;
  };

  // Selecting filters
  const handleSetCategory = (item) => {
    setCategory(item);
  };

  return (
    <Block background="panel">
      <Container ref={scrollRef}>
        {textAndImageObject && <TextAndImageBlock {...textAndImageObject} />}
        <Padding>
          <DropdownContainer>
            <Dropdown linksList={links} setCategory={handleSetCategory} />
          </DropdownContainer>
          <CategoryContainer>
            <CategoryBar links={links} setCategory={handleSetCategory} />
          </CategoryContainer>
        </Padding>
        <StyledArticleContainer>
          {currentCards.map((obj, ind) => {
            return (
              <ArticleCard
                key={ind}
                image={obj.image}
                category={obj.category}
                headline={obj.headline}
                date={obj.date}
                description={obj.description}
                cardFullWidth={true}
                url={obj.url}
                imageAspectRatio={1.618}
              />
            );
            // }
          })}
        </StyledArticleContainer>
        {totalNumberOfPages > 1 && (
          <PaginationContainer>
            <Pagination
              currentPage={currentPage}
              totalPages={totalNumberOfPages}
              pageSelected={handleSelectPage}
            />
          </PaginationContainer>
        )}
      </Container>
    </Block>
  );
};

const Container = styled.div``;

const StyledArticleContainer = styled.div`
  display: grid;
  grid-template-columns: 100%;
  grid-gap: 30px;
  @media (${query.atLeast('tablet')}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (${query.atLeast('desktop')}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Padding = styled.div`
  padding: 65px 0;
`;

const DropdownContainer = styled.div`
  @media (${query.atLeast('tablet')}) {
    display: none;
  }
`;

const CategoryContainer = styled.div`
  display: none;
  @media (${query.atLeast('tablet')}) {
    display: block;
  }
`;

const PaginationContainer = styled.div`
  justify-content: center;
  display: flex;
  ${mq({
    paddingTop: [
      theme.spacing.marginMobile,
      theme.spacing.marginTablet,
      theme.spacing.margin,
    ],
  })}
`;
